import React, { ComponentType } from 'react';
import { withHover } from '@team-griffin/stylr';
import Glyph, { IconName } from '@uds/glyphs';
import { ReactRouterLink } from '@uds/react-router-adapter';
import Badge, { Kind as BadgeKind } from '@uds/badge';
import { Colorway } from '@uds/theme-utils';
import callToAction from 'presentation/hocs/callToAction';
import createImageAlt from 'presentation/utils/createImageAlt';
import withLanguage from 'presentation/hocs/withLanguage';
import urlLanguageParse from 'presentation/utils/urlLanguageParse';
import { isNilOrEmpty } from 'presentation/utils/generalUtils';
import Shortcode, { ShortcodeProps } from 'components/Shortcode';
import { useIsExternalRoute } from 'domain/selectors/common';
import { isHashUrl } from 'domain/url';

import useStylesheet from './DesktopTier.stylesheet';

interface OuterProps {
  ribbonId?: string;
  colorway: Colorway;
  imageUrl?: string;
  badge?: ShortcodeProps;
  glyph?: IconName;
  selected?: boolean;
  behavior?: {
    type?: string;
    goto?: string;
  };
  label?: ShortcodeProps;
}

export interface InnerProps extends OuterProps {
  currentLanguage: string;
  handleClick?: (evt?: any) => void;
  hover: boolean;
}

function isValueDefined<T = unknown>(value: T | undefined): value is T {
  return !isNilOrEmpty(value);
}

export const PureDesktopTier = ({
  colorway,
  glyph,
  imageUrl,
  label,
  behavior,
  badge,
  selected,
  hover,
  handleClick,
  currentLanguage,
}: InnerProps) => {
  const { styles } = useStylesheet({
    colorway,
    selected,
    hover,
  });
  const hasGlyph = isValueDefined(glyph);
  const hasImage = isValueDefined(imageUrl);
  const hasBadge = isValueDefined(badge);
  const badgeKind = [Colorway.DARK, Colorway.HIGHLIGHT].includes(colorway) ? BadgeKind.PRIMARY : BadgeKind.HIGHLIGHT;
  const goto = behavior?.goto;
  const [ isExternalRoute, tierUrl ] = useIsExternalRoute(goto);

  return (
    <ReactRouterLink
      onClick={handleClick}
      href={tierUrl}
      scrollToRef={isHashUrl(tierUrl) ? tierUrl : undefined}
      style={styles.root}
      isExternalRoute={isExternalRoute}
      data-selector="tier-button"
    >
      {(hasGlyph || hasImage) && (
        <span style={styles.icon}>
          {hasGlyph && <Glyph name={glyph} size={styles.icon.width as number} color={styles.icon.color as string} />}
          {hasImage && (
            <img
              loading="lazy"
              src={urlLanguageParse(imageUrl, currentLanguage)}
              style={styles.image}
              alt={createImageAlt({ imageUrl })}
            />
          )}
        </span>
      )}

      {Boolean(label) && <Shortcode against={colorway} code={label} />}

      {hasBadge && (
        <span style={styles.badge}>
          <Badge flush={true} kind={badgeKind}>
            <Shortcode against={colorway} code={badge} />
          </Badge>
        </span>
      )}
    </ReactRouterLink>
  );
};

PureDesktopTier.displayName = 'DesktopTier';

export default callToAction(withLanguage(withHover(PureDesktopTier))) as ComponentType<OuterProps>;
